import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { MessageSquarePlus, Star, X, Lightbulb, Bug, FileQuestion, MessageCircle, Send } from 'lucide-react';

export const FEEDBACK_CATEGORIES = [
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb },
  { id: 'problem', label: 'Problem', icon: Bug },
  { id: 'missing_document', label: 'Missing document or topic', icon: FileQuestion },
  { id: 'other', label: 'Other', icon: MessageCircle },
];

const RATING_LABELS = ['Not useful', 'Slightly useful', 'Somewhat useful', 'Very useful', 'Extremely useful'];
const MAX_CHARS = 2000;

// With messageId the feedback is about that answer (question shown for context).
const FeedbackModal = ({ onClose, messageId = null, question = '' }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const shown = hover || rating;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error('Please choose a rating'); return; }
    if (!category) { toast.error('Please choose a feedback type'); return; }
    try {
      setSending(true);
      await axios.post('/api/feedback', {
        rating, category, message: message.trim() || null,
        ...(messageId ? { message_id: messageId } : {}),
      });
      toast.success('Thank you! Your feedback was sent to the admin team.');
      onClose();
    } catch (error) {
      const status = error.response?.status;
      toast.error(status === 429
        ? 'You have sent a lot of feedback recently. Please try again later.'
        : 'Could not send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-6 border-b border-gray-100 bg-[#171717] rounded-t-[1.25rem]">
          <div className="p-2.5 bg-white/10 rounded-xl">
            <MessageSquarePlus className="w-5 h-5 text-gray-300" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2 id="feedback-title" className="text-lg font-bold text-white">Give feedback</h2>
            <p className="text-xs text-gray-400">{messageId ? 'About this answer' : 'Helps us improve the assistant'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" noValidate>
          {question && (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 line-clamp-2">
              <span className="font-medium text-gray-500">Your question: </span>{question}
            </p>
          )}

          {/* Rating */}
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
              {messageId ? 'How useful was this answer?' : 'How useful is the assistant?'}
            </legend>
            <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  aria-label={`${n} star${n > 1 ? 's' : ''}: ${RATING_LABELS[n - 1]}`}
                  aria-pressed={rating === n}
                  className="p-1 rounded-md focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${n <= shown ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                    aria-hidden="true"
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500 min-h-[1.25rem]">{shown ? RATING_LABELS[shown - 1] : ''}</span>
            </div>
          </fieldset>

          {/* Type */}
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">Type of feedback</legend>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_CATEGORIES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setCategory(id)}
                  aria-pressed={category === id}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    category === id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          {/* Message */}
          <div>
            <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1.5">
              Message <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
              rows={4}
              placeholder="Tell us what worked, what didn't, or which document or topic is missing"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <p className="mt-1 text-right text-xs text-gray-400">{message.length}/{MAX_CHARS}</p>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" aria-hidden="true" />
            {sending ? 'Sending…' : 'Send feedback'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default FeedbackModal;
